import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Info, RefreshCw } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../Components/StakingRisk/ui/tooltip";
import { UpdateClassificationByIdThunk } from "../../../Store/Thunks/StakingRiskThunk";
import { RESET_ASSESSMENT_UPDATE } from "../../../Store/Reducers/StakingRiskReducer";
import { RISK_THRESHOLDS } from "../../../StakingRisk/constants/index";
import { getRiskLevelInfo } from "../../../StakingRisk/utils/RiskCalculator";
import { Slider } from "../../../Components/StakingRisk/ui/slider";
import { Label } from "../../../Components/StakingRisk/ui/label";
import { Input } from "../../../Components/StakingRisk/ui/input";
import { Button } from "../ui/button";


export default function ScoreSection({
  id,
  totalScorePercentage,
  riskScore,
  riskScoreType,
  updateProduct,
  manualRiskScore,
  weightedRisk,
  productInfo,
  automaticRiskScore
}) {
  const dispatch = useDispatch();

  const [localTotalScore, setLocalTotalScore] = useState(0);
  const [localRiskScore, setLocalRiskScore] = useState(0);
  const [manualLocalRiskScore, setManualLocalRiskScore] = useState(0);
  const [localRiskScoreType, setLocalRiskScoreType] = useState();
  const [isUseAuthomatic, setIsUseAuthomatic] = useState(true);
  const [isManualActive, setIsManualActive] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [weightedScore, setWeightedScore] = useState(0);
  const [existsWeightedScore, setExistsWeightedScore] = useState(0);
  const [localPercentageInput, setLocalPercentageInput] = useState();
  const assessmentupdate = useSelector((state) => state.StakingRiskReducer.assessmentupdate);
  const [saveStatus, setSaveStatus] = useState("idle"); // "idle" | "saving" | "success"

  useEffect(() => {
    setLocalRiskScoreType(riskScoreType);
    setLocalTotalScore(totalScorePercentage || localPercentageInput);
    setLocalRiskScore(riskScore)
    setManualLocalRiskScore(manualLocalRiskScore || manualRiskScore)
    setExistsWeightedScore(weightedScore || weightedRisk);
    setIsManualActive(false)
    dispatch({ type: RESET_ASSESSMENT_UPDATE })
  }
    , [riskScore, assessmentupdate])

  const useAutomaticScore = () => {
    if (riskScore > 0) {
      const newRiskScoreType = localRiskScoreType === "automatic" ? "manual" : "automatic";
      setLocalRiskScoreType(newRiskScoreType);
      setIsUseAuthomatic(true);
      setIsManualActive(false);
    }
  };
  const automaticRiskScoreValue = useMemo(() => {
    if (!!automaticRiskScore) {
      return automaticRiskScore;
    }
    return riskScore;
  }, [automaticRiskScore, riskScore]);

  const handleTotalScoreChange = async (value) => {
    setLocalTotalScore(value);
    setLocalPercentageInput(value);
    const updatedWeightedScore =
      localRiskScoreType === "automatic"
        ? riskScore > 0
          ? value * (riskScore - 1) / 2
          : 0
        : manualLocalRiskScore > 0
          ? value * (manualLocalRiskScore - 1) / 2
          : 0;
    setWeightedScore(updatedWeightedScore);
  };
  const handleRiskScoreChange = (value) => {
    setIsManualActive(true);
    setManualLocalRiskScore(value)
    setIsSaving(true);
    setSaveStatus("success")
    setLocalRiskScoreType("manual");
  };

  const handleSaveChanges = () => {
    if (!id) return;
    setSaveStatus("saving");
    setIsSaved(true);

    const weightedScore = !isManualActive
      ? Math.max(0, (localTotalScore * (localRiskScore - 1)) / 2 || 0)
      : Math.max(0, (localTotalScore * (manualLocalRiskScore - 1)) / 2 || 0);

    dispatch(
      UpdateClassificationByIdThunk(id, {
        totalScore: localTotalScore || 0,
        automaticRiskScore: localRiskScore || 0,
        automaticScoreLevel: getRiskLevelInfo(localRiskScore)?.level || "",
        manualRiskScore: manualLocalRiskScore || 0,
        manualScoreLevel: getRiskLevelInfo(manualLocalRiskScore)?.level || "",
        weightedScore: weightedScore ?? 0,
        riskScoreType: localRiskScoreType,
      })
    );

    updateProduct();
    setTimeout(() => setSaveStatus("success"), 300);
    setTimeout(() => setSaveStatus("idle"), 1500);
  };

  const classifications = useSelector(
    (state) => state.StakingRiskReducer?.stakingRiskClassifications || []
  );

  const totalWeightedScore =
    (Array.isArray(classifications)
      ? classifications.reduce((sum, c) => {
        // ensure it's a valid number
        const score = Number(c?.totalScore);
        if (isNaN(score)) return sum;
        // exclude this component’s id if it’s already in list
        if (c.id === id) return sum;
        return sum + score;
      }, 0)
      : 0) + (Number(localTotalScore) || 0);

  const allocatedScore = totalWeightedScore;

  // Fallback to 0 if still invalid
  const displayScore = isNaN(allocatedScore) ? 0 : allocatedScore;

  const displayWeighted = !isManualActive
    ? Math.max(0, localTotalScore * (localRiskScore - 1) / 2)
    : Math.max(0, localTotalScore * (manualLocalRiskScore - 1) / 2);

  return (
    <>
      <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50 mt-4">
        <h1
          className={`font-bold text-base flex justify-end ${Number(totalWeightedScore) > 100 ? "text-red-600" : "text-green-600"
            }`}
        >
          Allocated Score: {displayScore.toFixed(1)}{" "}
          {Number(totalWeightedScore) > 100 ? "out of 100%" : "from 100%"}
        </h1>

        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          Score Configuration
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="w-4 h-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Configure the percentage weight and risk score for this category</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </h3>
        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="total-score">% of Total Score</Label>
            <div className="flex items-center space-x-4">
              <Slider
                id="total-score"
                min={0}
                max={100}
                step={1}
                value={[localPercentageInput || localTotalScore || 0]}
                onValueChange={(value) => handleTotalScoreChange(value[0])}
                onValueCommit={(value) => handleTotalScoreChange(value[0])}
                className="flex-1"
              />
              <Input
                type="number"
                min="0"
                max="100"
                value={localPercentageInput || localTotalScore || 0}
                onChange={(e) => handleTotalScoreChange(Number(e.target.value))}
                className="w-20"
              />
              <span className="text-sm text-gray-500">%</span>
            </div>
          </div>
        </div>
      </div>
      <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50 mt-4">
        {automaticRiskScoreValue >= 0 && (
          <div className="space-y-2 ">
            <div className="flex justify-between">
              <div className="flex gap-3" style={{ flexDirection: "column" }}>
                <Label>Automatic Risk Score</Label>
                <div className="flex gap-3" >
                  <div className="text-2xl font-bold text-gray-700">
                    {parseFloat(automaticRiskScoreValue).toFixed(3)}
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskLevelInfo(automaticRiskScoreValue).bgColor} ${getRiskLevelInfo(automaticRiskScoreValue).textColor} ${getRiskLevelInfo(automaticRiskScoreValue).borderColor} border`}>
                    {getRiskLevelInfo(automaticRiskScoreValue).level}
                  </div>
                </div> </div>

              <div className="space-y-2 pr-2">
                <Label>Weighted Risk</Label>
                <div className="text-2xl font-bold text-indigo-600">
                  {isNaN(displayWeighted) ? "0.0" : displayWeighted.toFixed(1)}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="space-y-2">
          <div className="flex items-center justify-between" style={{ paddingTop: "4rem" }}>
            <Label htmlFor="risk-score">Manual Risk Score</Label>
            {isUseAuthomatic && (
              <Button
                variant="outline"
                size="sm"
                onClick={useAutomaticScore}
                className={`text-xs flex items-center ${(localRiskScoreType == 'automatic' && riskScoreType == 'automatic')
                  ? "border-gray-300 text-gray-500"
                  : "border-green-500 text-green-500"
                  }`}
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Use Automatic
              </Button>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <Slider
              id="risk-score"
              min={RISK_THRESHOLDS.MIN_SCORE}
              max={RISK_THRESHOLDS.MAX_SCORE}
              step={0.001}
              value={[
                !isManualActive
                  ? manualLocalRiskScore
                  : localRiskScoreType === "automatic"
                    ? riskScore
                    : manualLocalRiskScore
              ]}
              onValueChange={(value) => handleRiskScoreChange(value[0])}
              onValueCommit={(value) => handleRiskScoreChange(value[0])}
              className="flex-1"
            />

            <Input
              type="number"
              min={RISK_THRESHOLDS.MIN_SCORE}
              max={RISK_THRESHOLDS.MAX_SCORE}
              step="0.001"
              value={[
                !isManualActive
                  ? manualLocalRiskScore
                  : localRiskScoreType === "automatic"
                    ? riskScore
                    : manualLocalRiskScore
              ]} onChange={(e) => handleRiskScoreChange(Number(e.target.value))} className="w-20" />
          </div>
          <div className="flex gap-3 mt-2">
            <div className="text-lg font-semibold text-gray-700">
              {parseFloat(manualLocalRiskScore).toFixed(3)}

            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskLevelInfo(manualLocalRiskScore).bgColor} ${getRiskLevelInfo(manualLocalRiskScore).textColor} ${getRiskLevelInfo(manualLocalRiskScore).borderColor} border`}>
              {getRiskLevelInfo(manualLocalRiskScore).level}
            </div>
            {isManualActive && (
              <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                Manual Override
              </div>
            )}
          </div>
        </div>
        {/* Save Button */}
        <div className="mt-4 flex items-center gap-4">
          <Button onClick={handleSaveChanges} className="bg-indigo-600 text-white">
            Save Changes
          </Button>
          {saveStatus === "saving" && (
            <div className="text-sm text-gray-500 italic">Saving...</div>
          )}
          {saveStatus === "success" && (
            <div className="text-sm text-green-600">✔ Saved successfully</div>
          )}
        </div>

      </div>
    </>

  );
}